import { configureStore, combineReducers } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { persistReducer, persistStore } from "redux-persist";
import { AuthService } from "./../Services/AuthService";
import { CategoryService } from "./../Services/CategoryService";
import { RolesService } from "./../Services/RolesService";
import { EmployeeService } from "../Services/EmployeeService";
import { ProductService } from "../Services/ProductService";
import { CustomerService } from "../Services/CustomerService";
import { CouponService } from "../Services/CouponService";
import { OrderService } from "../Services/OrderService";
import {RefundService} from "../Services/RefundService"
import {CompanyProfileService} from "../Services/CompanyProfileService"
import { faqApi } from "../Services/FAQService";
import { SupplierService } from "../Services/SupplierService";
import { SettingsService } from "../Services/SettingsService";
import { CountryService } from "../Services/CountryService";
import { CountryTaxService } from "../Services/CountryTaxService";
import { ReportsService } from "../Services/ReportsService";
import { ServiceFeeService } from "../Services/ServiceFee";
import { BankTransfersService } from "../Services/BankTransfers";
import { AdminSettingService } from "../Services/AdminSettingService";
import storage from "redux-persist/lib/storage"; // Default storage (localStorage for web)

import AuthSlice from "./Slices/AuthSlice";
import NotificationSlice from "./Slices/NotificationSlice";
import CustomerPortalLayoutSlice from "./Slices/CustomerPortalLayoutSlice";
import { CustomerPortalLayoutService } from "./../Services/CustomerPortalLayoutService";
import { HelpService } from "./../Services/HelpService";
import { InventoryService } from "./../Services/InventoryService";
import SettingSlice from "./Slices/SettingSlice";
import { TaxationService } from "../Services/Taxation";
import { WalletService } from "../Services/Wallet";
import { DashboardApiService } from "../Services/DashboardService";

const settingPersistConfig = {
  key: "setting",
  storage,
};

// Combine reducers
const rootReducer = combineReducers({
  AuthSlice: AuthSlice.reducer,
  NotificationSlice: NotificationSlice.reducer,
  CustomerPortalLayoutSlice: CustomerPortalLayoutSlice.reducer,
  [AuthService.reducerPath]: AuthService.reducer,
  [CategoryService.reducerPath]: CategoryService.reducer,
  [RolesService.reducerPath]: RolesService.reducer,
  [EmployeeService.reducerPath]: EmployeeService.reducer,
  [ProductService.reducerPath]: ProductService.reducer,
  [CustomerService.reducerPath]: CustomerService.reducer,
  [CouponService.reducerPath]: CouponService.reducer,
  [OrderService.reducerPath]: OrderService.reducer,
  [RefundService.reducerPath]:RefundService.reducer,
  [CompanyProfileService.reducerPath]:CompanyProfileService.reducer,
  [faqApi.reducerPath]: faqApi.reducer,
  [SupplierService.reducerPath]: SupplierService.reducer,
  [SettingsService.reducerPath]: SettingsService.reducer,
  [CountryService.reducerPath]: CountryService.reducer,
  [TaxationService.reducerPath]: TaxationService.reducer,
  [CountryTaxService.reducerPath]: CountryTaxService.reducer,
  [CustomerPortalLayoutService.reducerPath]:
    CustomerPortalLayoutService.reducer,
  [HelpService.reducerPath]: HelpService.reducer,
  [InventoryService.reducerPath]: InventoryService.reducer,
  [ReportsService.reducerPath]: ReportsService.reducer,
  [ServiceFeeService.reducerPath]: ServiceFeeService.reducer,
  [WalletService.reducerPath]: WalletService.reducer,
  [BankTransfersService.reducerPath]: BankTransfersService.reducer,
  [AdminSettingService.reducerPath]: AdminSettingService.reducer,
  [DashboardApiService.reducerPath]: DashboardApiService.reducer,
  SettingSlice: persistReducer(settingPersistConfig, SettingSlice.reducer), // Persisted
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ["persist/PERSIST", "persist/REHYDRATE"],
      },
    }).concat(
      AuthService.middleware,
      CategoryService.middleware,
      RolesService.middleware,
      EmployeeService.middleware,
      ProductService.middleware,
      CustomerService.middleware,
      CouponService.middleware,
      OrderService.middleware,
      RefundService.middleware,
      CompanyProfileService.middleware,
      faqApi.middleware,
      SupplierService.middleware,
      SettingsService.middleware,
      CountryService.middleware,
      TaxationService.middleware,
      CountryTaxService.middleware,
      CustomerPortalLayoutService.middleware,
      HelpService.middleware,
      InventoryService.middleware,
      ReportsService.middleware,
      ServiceFeeService.middleware,
      WalletService.middleware,
      BankTransfersService.middleware,
      AdminSettingService.middleware,
      DashboardApiService.middleware
    ),
});

// Persistor
export const persistor = persistStore(store);

// Listeners
setupListeners(store.dispatch);

// Types
export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export default store;
